import { navigate, PageProps } from 'gatsby';
import { ComponentProps, type MouseEvent, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, FileAnalysisCard, Header, Icon, MenuButton, SidebarLayout, SidebarMenu } from '@opswat/ui';
import { PageHeader } from 'components/layout/PageHeader';
import { LicenseStatusBanner } from 'components/LicenseStatusBanner';
import { useQueryFilter } from 'hooks/useQueryFilter';
import { useUser } from 'services/User';
import { useUserData } from 'services/api/UserData';
import { markOrgDefaultScopeApplied } from 'services/api/orgScope';
import { DashboardTabs, IntegrationsTabs, SettingsTabs } from 'services/tabs';
import { EngineType } from '../../types/Workflows';
import { useGlobalDragDrop } from 'hooks/useGlobalDragDrop';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { canAccessPage, navigateToHomePage } from 'services/accessControl';
import { Logo } from 'components/layout/Logo';

const DEFAULT_WORKFLOW_ID = 'default';
const SidebarMenuItem = SidebarMenu.Item as (
    props: Omit<ComponentProps<typeof SidebarMenu.Item>, 'label'> & { label?: ReactNode },
) => ReturnType<typeof SidebarMenu.Item>;

type LayoutProps = Omit<PageProps, 'children'> & { children?: ReactNode };

export const Layout = ({ children, location }: LayoutProps) => {
    const pathname = location.pathname;
    const { t } = useTranslation();
    const { organizationIds, setGlobal } = useQueryFilter();
    const { user } = useUser();
    const { data: userData } = useUserData();
    const isPaidUser = userData?.licenseInfo?.paidUser === 1 || userData?.paid_user === 1;
    const organizationId = user?.organization?.organization_id;
    const { isDragOver, isUploading } = useGlobalDragDrop();

    useEffect(() => {
        setGlobal((prev) => {
            if ((!prev?.organizationIds || prev?.organizationIds?.length === 0) && organizationId) {
                return { ...prev, organizationIds: [organizationId] };
            }

            return prev;
        });
        // deps intentionally [organizationId]: setGlobal is recreated each render (not memoized), so it is omitted to avoid running the effect on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    // setGlobal goes through Gatsby's async navigate(), so the org lands in the URL a tick later.
    // Release the stats gate only once it is there, or the hooks fire a customer_id batch first (MDCI-1270).
    useEffect(() => {
        if (organizationIds.length > 0) {
            markOrgDefaultScopeApplied();
        }
    }, [organizationIds.length]);

    // Determine default settings tab based on user access
    const getDefaultSettingsTab = useCallback(() => {
        if (canAccessPage(user, 'profile')) return SettingsTabs.Profile;
        if (canAccessPage(user, 'security')) return SettingsTabs.Security;
        if (canAccessPage(user, 'notifications')) return SettingsTabs.Notifications;
        return SettingsTabs.Profile;
    }, [user]);

    const defaultSettingsTab = getDefaultSettingsTab();
    const canSeeStore = canAccessPage(user, 'store');

    // Filter menu items based on user role
    const menuData = useMemo(() => {
        const allMenuData: Array<ComponentProps<typeof SidebarMenu.Item> & { page?: string }> = [
            {
                label: t('Dashboard'),
                icon: 'dashboard',
                isActive: pathname.startsWith('/dashboard'),
                hasBorder: true,
                href: `/dashboard/${DashboardTabs.Security}`,
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate(`/dashboard/${DashboardTabs.Security}`);
                },
                page: 'dashboard',
            },
            {
                label: t('Analyze'),
                icon: 'fileProcessing',
                isActive: pathname.startsWith('/analyze'),
                href: '/analyze',
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate('/analyze');
                },
                page: 'analyze',
            },
            {
                label: t('Reports'),
                icon: 'detail-small',
                isActive: pathname.startsWith('/results') || pathname.startsWith('/reports'),
                hasBorder: true,
                href: '/reports',
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate('/reports');
                },
                page: 'reports',
            },
        ];

        return allMenuData.filter((item) => {
            // If no page property, show the item
            if (!item.page) return true;
            // Check if user has access to this page
            return canAccessPage(user, item.page);
        });
    }, [user, pathname, t]);

    const secondaryMenuData = useMemo((): Array<ComponentProps<typeof SidebarMenu.Item>> => {
        const settingsItem: ComponentProps<typeof SidebarMenu.Item> = {
            label: t('Settings'),
            icon: 'settings',
            isActive: pathname.startsWith('/settings'),
            href: `/settings/${defaultSettingsTab}`,
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate(`/settings/${defaultSettingsTab}`);
            },
        };

        const cveVulnerabilitiesItem: ComponentProps<typeof SidebarMenu.Item> = {
            label: t('CVE Vulnerabilities'),
            icon: 'base',
            isActive: pathname.startsWith('/cve-vulnerabilities'),
            href: '/cve-vulnerabilities',
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate('/cve-vulnerabilities');
            },
        };

        const licensingFreeItem: ComponentProps<typeof SidebarMenu.Item> = {
            label: t('Licensing'),
            icon: 'certificate',
            isActive: pathname.startsWith('/licensing'),
            href: '/licensing',
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate('/licensing');
            },
        };

        const apiKeyItem: ComponentProps<typeof SidebarMenu.Item> = {
            label: t('API Key'),
            icon: 'api',
            isActive: pathname.startsWith('/account/api-key'),
            href: '/account/api-key',
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate('/account/api-key');
            },
        };

        const integrationsItem: ComponentProps<typeof SidebarMenu.Item> = {
            label: t('Integrations'),
            icon: 'connect',
            isActive: pathname.startsWith('/integrations'),
            href: `/integrations/${IntegrationsTabs.All}`,
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate(`/integrations/${IntegrationsTabs.All}`);
            },
        };

        const items: Array<ComponentProps<typeof SidebarMenu.Item>> = [];

        // Group order: API Key, Integrations, CVE Vulnerabilities, Licensing, Store, Settings.
        if (canAccessPage(user, 'account/api-key')) {
            items.push(apiKeyItem);
        }
        if (canAccessPage(user, 'integrations')) {
            items.push(integrationsItem);
        }
        items.push(cveVulnerabilitiesItem, licensingFreeItem);

        // Store sits directly under Licensing and is only shown for online-store users.
        if (canSeeStore) {
            const storeItem: ComponentProps<typeof SidebarMenu.Item> = {
                label: t('Store'),
                icon: 'box',
                isActive: pathname.startsWith('/store'),
                href: '/store',
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate('/store');
                },
            };
            items.push(storeItem);
        }

        items.push(settingsItem);

        return items;
    }, [user, canSeeStore, defaultSettingsTab, pathname, t]);

    const workflowsMenuItems = useMemo(() => {
        const items = [
            {
                label: t('Workflows'),
                path: '/secure-pipeline-studio/workflows',
                isActive: pathname.startsWith('/secure-pipeline-studio/workflows'),
                page: 'workflows',
            },
            {
                label: t('Engines'),
                path: `/secure-pipeline-studio/engines/${EngineType.Filetype}/${DEFAULT_WORKFLOW_ID}`,
                isActive: pathname.startsWith('/secure-pipeline-studio/engines'),
                page: 'engines',
            },
        ];

        // Filter based on access control
        return items.filter((item) => canAccessPage(user, item.page));
    }, [user, pathname, t]);

    return (
        <div
            data-testid='layout-app-shell'
            className={`tw:font-roboto tw:relative tw:flex tw:flex-col tw:h-screen ${isDragOver || isUploading ? 'tw:opacity-50' : ''}`}
        >
            <div className='tw:pointer-events-none tw:absolute tw:top-0 tw:left-40 tw:z-10 tw:flex tw:h-60 tw:items-center'>
                <div className='tw:pointer-events-auto'>
                    <Logo />
                </div>
            </div>
            <SidebarLayout>
                <SidebarLayout.Header>
                    <Header.Actions>
                        <div className='tw:flex tw:items-center tw:gap-4'>
                            <MenuButton>
                                <MenuButton.Trigger
                                    onClick={() => navigateToHomePage(user)}
                                    data-testid='layout-home-button'
                                    className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                                >
                                    <Icon iconName='home' />
                                </MenuButton.Trigger>
                            </MenuButton>
                            <PageHeader />
                        </div>
                    </Header.Actions>
                </SidebarLayout.Header>

                <SidebarLayout.Menu>
                    <SidebarMenu>
                        {menuData.map((props) => (
                            <SidebarMenu.Item key={props.label} {...props} />
                        ))}
                        {isPaidUser && workflowsMenuItems.length > 0 && (
                            <SidebarMenu.Item
                                label={t('Secure Pipeline Studio')}
                                icon='target'
                                isActive={pathname.startsWith('/secure-pipeline-studio')}
                                hasBorder={true}
                                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                    // Navigate to first accessible workflow item
                                    const firstAccessiblePath = workflowsMenuItems[0]?.path;
                                    if (firstAccessiblePath) {
                                        navigate(firstAccessiblePath);
                                    }
                                }}
                            >
                                {workflowsMenuItems.map((item) => (
                                    <SidebarMenu.Item
                                        key={item.page}
                                        label={item.label}
                                        isActive={item.isActive}
                                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                            e.preventDefault();
                                            navigate(item.path);
                                        }}
                                    />
                                ))}
                            </SidebarMenu.Item>
                        )}
                        {!isPaidUser && (
                            <SidebarMenuItem
                                key='secure-pipeline-upsell'
                                label={
                                    <div className='tw:flex tw:items-center tw:justify-between tw:gap-8 tw:w-full'>
                                        <span>{t('Secure Pipeline')}</span>
                                        <Badge variant='secure' text={t('Paid')} />
                                    </div>
                                }
                                icon='target'
                                isActive={pathname.startsWith('/secure-pipeline-studio')}
                                hasBorder={true}
                                onClick={() => navigate('/secure-pipeline-studio/workflows')}
                            />
                        )}
                        {secondaryMenuData.map((props) => (
                            <SidebarMenu.Item key={props.label} {...props} />
                        ))}
                    </SidebarMenu>
                    <div className='tw:pt-8' data-sidebar-extra>
                        <FileAnalysisCard />
                    </div>
                </SidebarLayout.Menu>

                <SidebarLayout.Content className='tw:min-w-0'>
                    {/* This positioned wrapper is the containing block for absolutely positioned
                        descendants (e.g. @opswat/ui table `sr-only` labels). Without it they anchor to
                        the viewport, escape the scroll container's overflow and inflate the window's
                        scroll height, so `scrollIntoView` scrolls the window too and leaves blank space
                        below the content. It wraps the children instead of sitting on Content because
                        SidebarLayout.Content drops the `className` it is given. (MDCL-18164) */}
                    <div className='tw:relative'>
                        <LicenseStatusBanner />
                        {children}
                    </div>
                </SidebarLayout.Content>
            </SidebarLayout>

            {isUploading && (
                <div className='tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center'>
                    <LoadingSpinner />
                </div>
            )}
        </div>
    );
};
